import { Quote } from '@/model/quotesModel';
import { extractInvestmentMaxs, extractInvestmentMins, percentile } from '@/utils/investmentBounds';
import { useMemo } from 'react';
import type { PaymentOption, PremiumRange, SliderBounds } from '@/features/shop-insurance';

// investmentMin/Max are stored per quote row and a small tail carries implausible values, so
// the extremes are not safe slider bounds — a single row with a $3.4M max would otherwise
// raise the ceiling for the whole grid and let an advisor model an unissuable premium.
const BOUND_PERCENTILE = 0.95;

const DEFAULT_BOUNDS: SliderBounds = {
  min: 100000,
  max: 200000,
  midPoint: 150000,
  defaultValue: 100000,
  isCalculatedFromQuotes: false,
};

export function getPremiumRangeStep(max: number): number {
  return max <= 2_500 ? 10 : max <= 50_000 ? 100 : 1_000;
}

export function normalizePremiumRange(
  range: PremiumRange,
  bounds: Pick<SliderBounds, 'min' | 'max'>,
): PremiumRange {
  const first = Number.isFinite(range.min) ? range.min : bounds.min;
  const second = Number.isFinite(range.max) ? range.max : bounds.max;

  return {
    min: Math.max(bounds.min, Math.min(bounds.max, Math.min(first, second))),
    max: Math.max(bounds.min, Math.min(bounds.max, Math.max(first, second))),
  };
}

/**
 * Applies the annual-scale default for 5-Pay while preserving the existing
 * fallback chosen by each caller for every pre-existing payment option.
 */
export function getFallbackPremiumForPaymentOption(
  paymentOption: PaymentOption,
  existingFallbackPremium: number,
): number {
  return paymentOption === '5-Pay' ? 10_000 : existingFallbackPremium;
}

export function getDefaultPremiumRange(
  bounds: SliderBounds,
  paymentOption: PaymentOption,
): PremiumRange {
  const defaultMaximum =
    paymentOption === '10-Pay' || paymentOption === '20-Pay' ? 10_000 : 100_000;
  return normalizePremiumRange(
    {
      min: bounds.min,
      max: getFallbackPremiumForPaymentOption(paymentOption, defaultMaximum),
    },
    bounds,
  );
}

function matchesSliderTab(quote: Quote, selectedTab: ShopInsuranceTab): boolean {
  if (selectedTab === 'joint') {
    return quote.policyType === 'Joint Life';
  }
  // The Couples Discount tab was removed — its quotes are folded into the Single tab.
  return (
    quote.policyType === 'Single Life' ||
    quote.policyType === 'Standard' ||
    quote.policyType === 'Couples Discount'
  );
}

export function calculateSliderBounds(
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
  allowQuotedPremiumFallback = false,
): SliderBounds {
  if (!quotes || quotes.length === 0) {
    return DEFAULT_BOUNDS;
  }

  const filteredQuotes = quotes.filter(quote => matchesSliderTab(quote, selectedTab));

  if (filteredQuotes.length === 0) {
    return DEFAULT_BOUNDS;
  }

  const investmentMins = extractInvestmentMins(filteredQuotes);
  const investmentMaxs = extractInvestmentMaxs(filteredQuotes);

  if ((!investmentMins.length || !investmentMaxs.length) && !allowQuotedPremiumFallback) {
    return DEFAULT_BOUNDS;
  }

  const quotedPremiums = filteredQuotes
    .map(quote => quote.premiumAmount)
    .filter(premium => typeof premium === 'number' && premium > 0);

  if (!quotedPremiums.length && (!investmentMins.length || !investmentMaxs.length)) {
    return DEFAULT_BOUNDS;
  }

  // Percentiles keep outlier bounds from setting the range. If a cohort has no validated
  // bounds, its actually-quoted premiums still form a truthful fixed-price fallback range.
  const boundsMin = investmentMins.length
    ? percentile(investmentMins, 1 - BOUND_PERCENTILE, 'up')!
    : Math.min(...quotedPremiums);
  const boundsMax = investmentMaxs.length
    ? percentile(investmentMaxs, BOUND_PERCENTILE, 'down')!
    : Math.max(...quotedPremiums);

  const finalSliderMin = quotedPremiums.reduce((low, premium) => Math.min(low, premium), boundsMin);
  const finalSliderMax = quotedPremiums.reduce(
    (high, premium) => Math.max(high, premium),
    boundsMax,
  );

  if (finalSliderMax <= finalSliderMin) {
    if (!allowQuotedPremiumFallback) return DEFAULT_BOUNDS;
    const step = getPremiumRangeStep(finalSliderMax);
    return {
      min: Math.max(0, finalSliderMin - step),
      max: finalSliderMax + step,
      midPoint: finalSliderMin,
      defaultValue: finalSliderMin,
      isCalculatedFromQuotes: true,
    };
  }

  const midPoint = Math.floor((finalSliderMin + finalSliderMax) / 2);

  const defaultValue =
    filteredQuotes[0].premiumAmount > 0
      ? Math.max(finalSliderMin, Math.min(finalSliderMax, filteredQuotes[0].premiumAmount))
      : midPoint;

  return {
    min: finalSliderMin,
    max: finalSliderMax,
    midPoint,
    defaultValue,
    isCalculatedFromQuotes: true,
  };
}

export const useSliderBounds = (
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
  allowQuotedPremiumFallback = false,
): SliderBounds => {
  return useMemo(() => {
    try {
      return calculateSliderBounds(quotes, selectedTab, allowQuotedPremiumFallback);
    } catch (error) {
      console.error('Error calculating slider bounds:', error);
      return DEFAULT_BOUNDS;
    }
  }, [allowQuotedPremiumFallback, quotes, selectedTab]);
};
